import { useState } from "react";
import { SupplierFormValues } from "@templates/ZSuppliers/ZDrawers/ZAddOrEditSupplier/interface";
import { AddressValues } from "@/Contracts";
import {
	SupplierCreateRequest,
	SupplierUpdateRequest
} from "@/Contracts/Settings/index";
import useDispatch from "@/Components/Utils/useDispatch";
import SettingsActionCreators from "@/Actions/Settings";
import { ZMessage } from "@molecules";
import { ListPagePostRequest } from "@/Contracts";
import { ReturnSupplierMap } from "./interface";

/**
 * Includes stateful logic for adding a supplier or getting a list of suppliers.
 * @returns An object with functions & states, which can be called/used to add/get suppliers.
 *
 * @author Idrees
 */
export const useAddOrEditSupplier = (): ReturnSupplierMap => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [currentSupplierId, setCurrentSupplierId] = useState<string>();
	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useDispatch();

	const getSuppliers = async (
		pagination?: ListPagePostRequest,
		searchQuery?: string
	) => {
		try {
			setIsLoading(true);
			const supplierResponse = await dispatch(
				SettingsActionCreators.getSuppliers(pagination, searchQuery)
			);
			setIsLoading(false);
			return supplierResponse.suppliers;
		} catch (error) {
			setIsLoading(false);
			console.error(error);
		}
		return;
	};

	const getAddress = (supplierValues: SupplierFormValues) => {
		const address: AddressValues = {};

		if (supplierValues.city) address.city = supplierValues.city;
		if (supplierValues.country) address.country = supplierValues.country;
		if (supplierValues.zip_code) address.pincode = supplierValues.zip_code;
		if (supplierValues.state) address.state = supplierValues.state;
		if (supplierValues.address_line_1)
			address.address_line_1 = supplierValues.address_line_1;
		if (supplierValues.address_line_2)
			address.address_line_2 = supplierValues.address_line_2;

		return address;
	};

	const addSupplier = async (supplierValues: SupplierFormValues) => {
		const address = getAddress(supplierValues);
		const supplierCreateRequest = new SupplierCreateRequest(
			supplierValues.supplier_name,
			supplierValues.has_drop_ship,
			supplierValues.email,
			address
		);

		try {
			setIsSubmitting(true);
			const supplierResponse = await dispatch(
				SettingsActionCreators.addSupplier(supplierCreateRequest)
			);
			ZMessage.success("New supplier added successfully");
			dispatch(SettingsActionCreators.hideAddOrEditSupplierDrawer());
			return supplierResponse.supplier;
		} catch (error) {
			console.error(error);
		} finally {
			setIsSubmitting(false);
		}

		return;
	};

	const editSupplier = async (supplierValues: SupplierFormValues) => {
		const address = getAddress(supplierValues);
		const supplierUpdateRequest = new SupplierUpdateRequest(
			supplierValues.supplier_name,
			supplierValues.has_drop_ship,
			supplierValues.email,
			address
		);

		try {
			setIsSubmitting(true);
			const supplierResponse = await dispatch(
				SettingsActionCreators.updateSupplier(
					currentSupplierId,
					supplierUpdateRequest
				)
			);
			ZMessage.success("Supplier updated successfully");
			dispatch(SettingsActionCreators.hideAddOrEditSupplierDrawer());
			return supplierResponse.supplier;
		} catch (error) {
			console.error(error);
		} finally {
			setIsSubmitting(false);
		}

		return;
	};

	const getSupplierById = async (selectedSupplierId?: string | string[]) => {
		const selectedIds = Array.isArray(selectedSupplierId)
			? selectedSupplierId.join(",")
			: selectedSupplierId;

		try {
			setIsLoading(true);
			const supplierResponse = await dispatch(
				SettingsActionCreators.getSuppliers(
					undefined,
					undefined,
					selectedIds
				)
			);
			setIsLoading(false);
			return supplierResponse.suppliers;
		} catch (error) {
			setIsLoading(false);
			console.error(error);
		}

		return;
	};

	return {
		addSupplier,
		editSupplier,
		getSuppliers,
		getSupplierById,
		isSubmitting,
		isLoading,
		currentSupplierId,
		setCurrentSupplierId,
		setIsLoading
	};
};
